import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const AuthContext = createContext(null);

function baseUnauthState() {
  return {
    status: "unauthenticated",
    isAuthenticated: false,
    user: null,
    memberships: [],
    availableAreas: [],
    defaultAreaId: null,
    currentAreaId: null,
    currentAreaName: null,
    currentAreaMembership: null,
    currentAreaModules: {},
    canAccessAnyAdmin: false,
    canAccessAdmin: false,
    canAccessAnalytics: false,
    canAccessEducation: false,
    canAccessTrends: false,
    canAccessTsunami: false,
    canAccessLandmarks: false,
    canAccessCandidates: false,
    canAccessReports: false,
    canAccessQuarters: false,
    canAccessTesting: false,
    adminForbidden: false,
    error: "",
  };
}

function toPositiveIntOrNull(value) {
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) return null;
  return n;
}

function normalizeArea(areaLike) {
  const rawId =
    areaLike && Object.prototype.hasOwnProperty.call(areaLike, "id") ? areaLike.id : areaLike && areaLike.area_id;
  const id = toPositiveIntOrNull(rawId);
  if (id === null) return null;
  const rawName =
    areaLike && typeof areaLike.name === "string" && areaLike.name.trim() ? areaLike.name : areaLike && areaLike.area_name;
  const name = typeof rawName === "string" && rawName.trim() ? rawName.trim() : `Area ${id}`;
  return {
    id,
    name,
  };
}

function toSessionState(payload) {
  const user = payload && payload.user ? payload.user : null;
  const memberships = Array.isArray(payload && payload.memberships) ? payload.memberships : [];
  const activeMemberships = memberships.filter(
    (m) => m && Boolean(m.is_active) === true && Number.isFinite(Number(m.area_id))
  );
  const membershipAreas = activeMemberships
    .map((membership) =>
      normalizeArea({
        id: membership.area_id,
        name: membership.area_name,
      })
    )
    .filter(Boolean);
  const availableAreasFromPayload = Array.isArray(payload && payload.available_areas)
    ? payload.available_areas.map(normalizeArea).filter(Boolean)
    : [];
  const availableAreas = (availableAreasFromPayload.length > 0 ? availableAreasFromPayload : membershipAreas).filter(
    (area, index, arr) => arr.findIndex((candidate) => candidate.id === area.id) === index
  );

  const defaultAreaIdFromPayload = toPositiveIntOrNull(payload && payload.default_area_id);
  const defaultArea =
    defaultAreaIdFromPayload === null
      ? null
      : availableAreas.find((area) => area.id === defaultAreaIdFromPayload) || null;

  let defaultAreaId = defaultArea ? defaultArea.id : null;
  if (defaultAreaId === null && availableAreas.length > 0) {
    defaultAreaId = availableAreas[0].id;
  }

  const currentAreaFromPayload = normalizeArea(payload && payload.current_area);
  const currentArea =
    (currentAreaFromPayload &&
      (availableAreas.find((area) => area.id === currentAreaFromPayload.id) || currentAreaFromPayload)) ||
    (defaultAreaId === null ? null : availableAreas.find((area) => area.id === defaultAreaId) || null) ||
    null;
  const currentAreaId = currentArea ? currentArea.id : null;
  const currentAreaName = currentArea ? currentArea.name : null;
  const currentAreaMembership =
    currentAreaId === null
      ? null
      : activeMemberships.find((m) => Number(m.area_id) === currentAreaId) || null;
  const currentAreaModules =
    currentAreaMembership && currentAreaMembership.modules && typeof currentAreaMembership.modules === "object"
      ? currentAreaMembership.modules
      : {};

  const isSuperadmin = Boolean(user && user.is_superadmin);
  const hasAdminMembership = activeMemberships.some((m) => m && m.role === "admin");
  const canAccessAnyAdmin = isSuperadmin || hasAdminMembership;
  const canAccessAdmin = Boolean(
    isSuperadmin || (currentAreaMembership && currentAreaMembership.role === "admin")
  );
  const canAccessAnalytics = Boolean(isSuperadmin || currentAreaModules.analytics);
  const canAccessEducation = Boolean(isSuperadmin || currentAreaModules.education);
  const canAccessTrends = Boolean(isSuperadmin || currentAreaModules.trends);
  const canAccessTsunami = Boolean(isSuperadmin || currentAreaModules.tsunami);
  const canAccessLandmarks = Boolean(isSuperadmin || currentAreaModules.landmarks);
  const canAccessCandidates = Boolean(isSuperadmin || currentAreaModules.candidates);
  const canAccessReports = Boolean(isSuperadmin || currentAreaModules.reports);
  const canAccessQuarters = Boolean(isSuperadmin || currentAreaModules.quarters);
  const canAccessTesting = Boolean(isSuperadmin || currentAreaModules.testing);

  return {
    status: "authenticated",
    isAuthenticated: true,
    user,
    memberships,
    availableAreas,
    defaultAreaId,
    currentAreaId,
    currentAreaName,
    currentAreaMembership,
    currentAreaModules,
    canAccessAnyAdmin,
    canAccessAdmin,
    canAccessAnalytics,
    canAccessEducation,
    canAccessTrends,
    canAccessTsunami,
    canAccessLandmarks,
    canAccessCandidates,
    canAccessReports,
    canAccessQuarters,
    canAccessTesting,
    adminForbidden: false,
    error: "",
  };
}

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, {
    credentials: "include",
    ...options,
  });
  const data = await parseJsonSafe(response);

  if (!response.ok) {
    const message =
      (data && typeof data.error === "string" && data.error) || `Request failed (${response.status})`;
    const error = new Error(message);
    error.status = response.status;
    error.payload = data;
    throw error;
  }

  return data;
}

export function resolveAppPath(sessionLike) {
  if (!sessionLike || !sessionLike.isAuthenticated) return "/app/login";
  if (toPositiveIntOrNull(sessionLike.currentAreaId)) return "/app";
  const availableAreas = Array.isArray(sessionLike.availableAreas) ? sessionLike.availableAreas : [];
  if (availableAreas.length > 0) return "/app";
  return "/app/waiting";
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => ({
    ...baseUnauthState(),
    status: "loading",
  }));

  const refreshSession = useCallback(async () => {
    try {
      const payload = await requestJson("/api/auth/me");
      const next = toSessionState(payload);
      setSession(next);
      return next;
    } catch (error) {
      if (error && error.status === 401) {
        const next = baseUnauthState();
        setSession(next);
        return next;
      }
      const next = {
        ...baseUnauthState(),
        status: "error",
        error: error && error.message ? String(error.message) : "Unable to verify session",
      };
      setSession(next);
      return next;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = await refreshSession();
      if (cancelled) return;
      setSession((prev) => {
        if (prev.status === "loading") return next;
        return prev;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshSession]);

  const login = useCallback(
    async ({ email, password }) => {
      await requestJson("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      return refreshSession();
    },
    [refreshSession]
  );

  const logout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (_) {
      // If logout request fails, client still drops local auth state.
    }
    const next = baseUnauthState();
    setSession(next);
    return next;
  }, []);

  const markAdminForbidden = useCallback(() => {
    setSession((prev) => {
      if (!prev || !prev.isAuthenticated || prev.adminForbidden) return prev;
      return {
        ...prev,
        adminForbidden: true,
      };
    });
  }, []);

  const value = useMemo(
    () => ({
      ...session,
      refreshSession,
      login,
      logout,
      markAdminForbidden,
    }),
    [session, refreshSession, login, logout, markAdminForbidden]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
}
